import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HotelIcon from '@mui/icons-material/Hotel';
import GroupsIcon from '@mui/icons-material/Groups';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { getStaticFileUrl } from '../../utils/staticUtils';
import { AUTH_LOGO_STATIC_PATH } from '../auth/loginDarkTheme';
import './StartupSequence.css';

const SPLASH_MESSAGES = [
  'Loading Workspace...',
  'Connecting PMS...',
  'Syncing Channels...',
  'Preparing Dashboard...',
];

const PARTICLES = Array.from({ length: 18 }, (_, index) => ({
  id: index,
  left: `${8 + ((index * 17) % 84)}%`,
  top: `${10 + ((index * 23) % 78)}%`,
  delay: `${(index % 6) * 180}ms`,
  duration: `${3600 + (index % 5) * 520}ms`,
}));

function HotelIllustration() {
  return (
    <Box className="rn-startup-hotel" aria-hidden="true">
      <Box className="rn-startup-hotel-glow" />
      <Box className="rn-startup-hotel-building rn-startup-hotel-building--back" />
      <Box className="rn-startup-hotel-building rn-startup-hotel-building--front">
        {Array.from({ length: 9 }, (_, index) => (
          <Box key={index} className="rn-startup-hotel-window" />
        ))}
        <Typography className="rn-startup-hotel-label">HOTEL</Typography>
      </Box>
      <Box className="rn-startup-hotel-base" />
    </Box>
  );
}

function OrbitIcons() {
  const icons = [
    { Icon: CalendarMonthIcon, label: 'Calendar' },
    { Icon: TrendingUpIcon, label: 'Revenue' },
    { Icon: HotelIcon, label: 'Reservation' },
    { Icon: GroupsIcon, label: 'Guests' },
  ];

  return (
    <Box className="rn-startup-orbit" aria-label="Hotel platform modules">
      {icons.map(({ Icon, label }, index) => (
        <Box key={label} className={`rn-startup-orbit-icon rn-startup-orbit-icon--${index}`}>
          <Icon fontSize="small" />
          <span className="sr-only">{label}</span>
        </Box>
      ))}
    </Box>
  );
}

export default function StartupSequence({ onComplete }) {
  const [phase, setPhase] = useState('splash');
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const timers = [
      window.setTimeout(() => setPhase('exit'), 2800),
      window.setTimeout(() => onComplete?.(), 3100),
    ];
    return () => timers.forEach(window.clearTimeout);
  }, [onComplete]);

  useEffect(() => {
    if (phase !== 'splash') return undefined;
    const interval = window.setInterval(() => {
      setMessageIndex((index) => (index + 1) % SPLASH_MESSAGES.length);
    }, 500);
    return () => window.clearInterval(interval);
  }, [phase]);

  const logoSrc = useMemo(() => getStaticFileUrl(AUTH_LOGO_STATIC_PATH), []);

  return (
    <Box className={`rn-startup-overlay rn-startup-overlay--${phase}`} role="status" aria-live="polite">
      <Box className="rn-startup-bg">
        <Box className="rn-startup-grid" />
        <Box className="rn-startup-radial rn-startup-radial--one" />
        <Box className="rn-startup-radial rn-startup-radial--two" />
        {PARTICLES.map((particle) => (
          <span
            key={particle.id}
            className="rn-startup-particle"
            style={{
              left: particle.left,
              top: particle.top,
              animationDelay: particle.delay,
              animationDuration: particle.duration,
            }}
          />
        ))}
      </Box>

      <Box className="rn-startup-splash">
        <Box className="rn-startup-logo-wrap">
          <Box className="rn-startup-logo-glow" />
          <img className="rn-startup-logo" src={logoSrc} alt="RoomNexa" />
        </Box>
        <Typography className="rn-startup-wordmark">roomnexa</Typography>
        <Box className="rn-startup-tag">
          <span className="rn-startup-tag-icon" aria-hidden="true">
            <AutoAwesomeIcon sx={{ fontSize: 14 }} />
          </span>
          <span>AI Hospitality Platform</span>
        </Box>
        <Typography className="rn-startup-subtitle">Smart. Simple. Seamless.</Typography>

        <Box className="rn-startup-stage">
          <OrbitIcons />
          <HotelIllustration />
        </Box>

        <Box className="rn-startup-progress">
          <Box className="rn-startup-progress-track">
            <Box className="rn-startup-progress-bar" />
          </Box>
          <Typography className="rn-startup-message">{SPLASH_MESSAGES[messageIndex]}</Typography>
        </Box>
      </Box>
    </Box>
  );
}
